/**
 * Visual Icon Picker - browse & select a lucide-react icon by clicking.
 * Curated list of ~50 icons relevant to hospitality / guest platforms.
 */
import React, { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown, Search } from "lucide-react";

const CURATED = [
  // Apartments / places
  "Home", "House", "Building2", "Building", "BedDouble", "Bed", "Sofa", "Key", "KeyRound", "DoorOpen",
  // Nature / brand
  "Leaf", "Flower", "Flower2", "TreePine", "TreePalm", "Trees", "Sprout", "Sun", "Sunset",
  // Categories - things to do
  "Map", "MapPin", "Compass", "Camera", "Landmark", "Mountain", "Waves", "Sailboat", "Ship", "Bike",
  // Food & drink
  "UtensilsCrossed", "Utensils", "Pizza", "Coffee", "IceCream", "IceCream2", "Wine", "Beer", "GlassWater",
  // Shopping / services
  "ShoppingBasket", "ShoppingBag", "ShoppingCart", "Store", "Croissant",
  // Health / emergency
  "Cross", "Pill", "Stethoscope", "HeartPulse", "Siren", "AlertTriangle", "Ambulance", "Phone", "PhoneCall",
  // Transport
  "Bus", "Car", "Train", "TrainFront", "Plug", "Fuel", "ParkingCircle", "TramFront",
  // Utility
  "WashingMachine", "Wifi", "Zap", "Cloud", "CloudSun", "CloudRain", "Thermometer", "Umbrella",
  // Info / misc
  "Info", "BookOpen", "Newspaper", "Star", "Sparkles", "Gift", "Heart", "MessageCircle", "Calendar",
];

// Filter to icons that actually exist in the current lucide-react build.
const AVAILABLE = CURATED.filter((n) => Boolean(Icons[n]));

export default function IconPicker({ value, onChange, label = "Icona", testid = "icon-picker" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const Cur = value && Icons[value] ? Icons[value] : Icons.CircleDashed;

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return AVAILABLE;
    return AVAILABLE.filter((n) => n.toLowerCase().includes(query));
  }, [q]);

  return (
    <div>
      {label && (
        <label className="text-[11px] uppercase tracking-overline font-semibold text-ink-500 block mb-1.5">{label}</label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          data-testid={testid}
          className="w-full inline-flex items-center gap-3 rounded-xl bg-white border border-ink-200/50 px-3 py-2.5 hover:border-bronze-300 transition"
        >
          <div className="w-9 h-9 rounded-lg bg-bronze-50 flex items-center justify-center">
            <Cur strokeWidth={1.5} className="w-4 h-4 text-bronze-400" />
          </div>
          <span className="flex-1 text-sm text-ink-700 text-left truncate">{value || "Seleziona un'icona"}</span>
          <ChevronDown className="w-4 h-4 text-ink-300" />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[340px] p-3 bg-white border-ink-200/50">
          <div className="flex items-center gap-2 mb-2 rounded-lg bg-ivory-100 px-2.5 py-1.5">
            <Search className="w-3.5 h-3.5 text-ink-300" />
            <Input
              placeholder="Cerca icona..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-7 border-0 bg-transparent focus-visible:ring-0 p-0 text-sm placeholder:text-ink-300"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="grid grid-cols-6 gap-1.5 max-h-72 overflow-y-auto hide-scrollbar">
            {filtered.map((name) => {
              const Ic = Icons[name];
              const active = value === name;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => {
                    onChange(name);
                    setOpen(false);
                  }}
                  data-testid={`${testid}-option-${name}`}
                  title={name}
                  className={`relative aspect-square rounded-lg flex items-center justify-center transition ${
                    active
                      ? "bg-bronze-100 text-bronze-500"
                      : "bg-ivory-50 hover:bg-ivory-200 text-ink-700"
                  }`}
                >
                  <Ic strokeWidth={1.4} className="w-4 h-4" />
                  {active && <Check className="absolute top-1 right-1 w-2.5 h-2.5 text-bronze-500" />}
                </button>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <p className="text-xs text-ink-300 text-center py-4">Nessuna icona trovata</p>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
